import React, { useState } from 'react';
import { Copy, Check, AlertTriangle, CheckCircle2, ExternalLink, Package } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import {
  GHL_PRODUCT_SPECS,
  GHL_VALUES_NEEDED,
  PAYMENT_WEBHOOK_URL,
  returnUrlFor,
  webhookPayloadTemplate,
  SITE_ORIGIN,
} from '@/data/webhookSetup';
import { money } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * GOHIGHLEVEL PRODUCT BUILD SHEET (Admin > Payments)
 * ───────────────────────────────────────────────────────────────────────────
 * The operator builds the products inside GoHighLevel; this panel is the
 * exact, field-by-field specification of what to build and the ONE value to
 * send back per product (the Payment Link URL -> env var).
 *
 * Every number here is read from GHL_PRODUCT_SPECS in src/data/webhookSetup.ts,
 * which mirrors PLAN_INCLUDED_LEADS / SINGLE_LEAD_PRODUCT in constants.ts and
 * PLAN_LEADS in the payment-webhook + checkout-link edge functions. Nothing is
 * retyped in this component, so Premium can never say 20 here and 25 there.
 */

const CopyBtn: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(value).then(
          () => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1600);
          },
          () => undefined
        );
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 px-2 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50"
    >
      {done ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
      {label || (done ? 'Copied' : 'Copy')}
    </button>
  );
};

const Row: React.FC<{ k: string; v: string; copy?: boolean }> = ({ k, v, copy }) => (
  <div className="flex items-start justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
    <span className="text-xs font-semibold text-slate-500">{k}</span>
    <span className="flex items-center gap-2 text-right">
      <span className="break-all text-xs font-bold text-slate-900">{v}</span>
      {copy && <CopyBtn value={v} />}
    </span>
  </div>
);

const AdminGhlProducts: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [openPlan, setOpenPlan] = useState<string>('single');

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <Package className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">
            {es ? 'Productos de GoHighLevel — qué crear' : 'GoHighLevel products — what to build'}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            {es
              ? 'Especificación campo por campo de los cuatro productos. Cree cada uno en Pagos > Productos y devuelva únicamente la URL del enlace de pago.'
              : 'Field-by-field specification for all four products. Build each one in Payments > Products and send back only the Payment Link URL.'}
          </p>
        </div>
      </div>

      {/* ---- Product tabs ------------------------------------------------ */}
      <div className="mt-5 flex flex-wrap gap-2">
        {GHL_PRODUCT_SPECS.map((p) => (
          <button
            key={p.plan}
            type="button"
            onClick={() => setOpenPlan(p.plan)}
            className={`rounded-lg px-3 py-2 text-xs font-bold capitalize transition-colors ${
              openPlan === p.plan
                ? 'bg-slate-900 text-white'
                : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {p.plan} · {money(p.amountCents)}
          </button>
        ))}
      </div>

      {GHL_PRODUCT_SPECS.filter((p) => p.plan === openPlan).map((p) => (
        <div key={p.plan} className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <Row k={es ? 'Nombre del producto' : 'Product name'} v={p.productName} copy />
          <Row k={es ? 'Tipo' : 'Product type'} v={p.productType} />
          <Row k={es ? 'Precio' : 'Price'} v={p.priceLabel} />
          <Row
            k={es ? 'Intervalo' : 'Billing interval'}
            v={p.interval ? p.interval : es ? 'Pago único' : 'One time'}
          />
          <Row
            k={es ? 'Número de pagos' : 'Number of payments'}
            v={p.billingCycles ? String(p.billingCycles) : es ? 'No aplica' : 'N/A'}
          />
          <Row k={es ? 'Créditos otorgados' : 'Credits granted'} v={String(p.credits)} />
          <Row
            k={es ? 'Campos obligatorios' : 'Required checkout fields'}
            v={es ? 'Correo (clave de coincidencia), Nombre, Apellido' : 'Email (match key), First name, Last name'}
          />
          <Row
            k={es ? 'Campos personalizados' : 'Custom fields'}
            v={es ? 'Ninguno — no agregue ninguno' : 'None — do not add any'}
          />
          <Row
            k={es ? 'Metadatos' : 'Metadata'}
            v={`plan=${p.plan} (${es ? 'opcional' : 'optional'})`}
          />
          <Row k={es ? 'URL de éxito (EN)' : 'Success URL (EN)'} v={returnUrlFor(p.plan, 'en')} copy />
          <Row k={es ? 'URL de éxito (ES)' : 'Success URL (ES)'} v={returnUrlFor(p.plan, 'es')} copy />
          <Row
            k={es ? 'URL de cancelación' : 'Cancel URL'}
            v={p.plan === 'single' ? `${SITE_ORIGIN}/en/jobs` : `${SITE_ORIGIN}/en/pricing`}
            copy
          />
          <Row k="Webhook URL" v={PAYMENT_WEBHOOK_URL} copy />
          <Row
            k={es ? 'Variable de entorno' : 'Environment variable'}
            v={p.envName}
            copy
          />

          <div className="mt-3">
            <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">
              {es ? 'Cuerpo JSON del webhook' : 'Webhook JSON body'}
            </p>
            <pre className="max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
              {webhookPayloadTemplate(p.plan)}
            </pre>
            <div className="mt-2">
              <CopyBtn value={webhookPayloadTemplate(p.plan)} label={es ? 'Copiar JSON' : 'Copy JSON'} />
            </div>
          </div>

          <ul className="mt-3 space-y-1.5">
            {p.notes.map((n, i) => (
              <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                {n}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* ---- The only values we need back -------------------------------- */}
      <div className="mt-6">
        <h3 className="text-sm font-extrabold text-slate-900">
          {es ? 'Valores que GigGone necesita de vuelta' : 'Values GigGone needs back'}
        </h3>
        <div className="mt-2 space-y-2">
          {GHL_VALUES_NEEDED.map((v) => (
            <div
              key={v.key}
              className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5"
            >
              <div>
                <p className="text-xs font-extrabold text-slate-900">{v.label}</p>
                <p className="mt-0.5 font-mono text-[11px] text-slate-500">{v.key}</p>
                <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{v.note}</p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> {es ? 'guardado' : 'stored'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-5 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {es
          ? 'Los pagos con tarjeta dentro de la app están deshabilitados en la pasarela, así que el pago alojado de GoHighLevel es la ÚNICA forma de cobrar el producto de $29. El enlace debe existir antes de que el botón pueda cobrar.'
          : 'In-app card payments are disabled at the gateway, so GoHighLevel hosted checkout is the ONLY way the $29 product can take money. The link must exist before that button can charge anything.'}
      </p>
    </section>
  );
};

export default AdminGhlProducts;
